import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Form, Input, Button, message, Result } from 'antd'
import { MailOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons'
import { sendCode, resetPassword } from '../../services/auth'
import EmailCodeVerification from '../../components/common/EmailCodeVerification'
import { useEmailCodeVerification } from '../../hooks/useEmailCodeVerification'

interface ResetPasswordForm {
  email: string
  code: string
  newPassword: string
  confirmPassword: string
}

export default function ResetPassword() {
  const [loading, setLoading] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [success, setSuccess] = useState(false)
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const email = Form.useWatch('email', form)
  const emailVerification = useEmailCodeVerification(email?.trim() ?? '')

  const handleSendCode = async () => {
    try {
      await form.validateFields(['email'])
      const email = form.getFieldValue('email')
      setSendingCode(true)
      await emailVerification.send((token) => sendCode(email, 'reset', token))
      message.success('驗證碼已發送。如果沒有收到，請先檢查垃圾郵箱。')
      setCountdown(60)
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '發送驗證碼失敗')
    } finally {
      setSendingCode(false)
    }
  }

  const handleSubmit = async (values: ResetPasswordForm) => {
    if (!emailVerification.ready) {
      message.warning('請先完成人機驗證')
      return
    }
    if (values.newPassword !== values.confirmPassword) {
      message.error('兩次輸入的密碼不一致')
      return
    }
    setLoading(true)
    try {
      await resetPassword(values.email, values.code, values.newPassword)
      setSuccess(true)
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '重置密碼失敗')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <Result
            status="success"
            title="密碼重置成功"
            subTitle="您的密碼已成功重置，請使用新密碼登入。"
            extra={
              <Button type="primary" onClick={() => navigate('/login')}>
                返回登入
              </Button>
            }
          />
        </div>
      </div>
    )
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-title">
          <h1>重置密碼</h1>
          <p>輸入您的郵箱以重置密碼</p>
        </div>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: '請輸入郵箱' },
              { type: 'email', message: '請輸入有效的郵箱地址' },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="郵箱"
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="code"
            rules={[{ required: true, message: '請輸入驗證碼' }]}
          >
            <Input
              prefix={<SafetyOutlined />}
              placeholder="驗證碼"
              size="large"
              suffix={
                <Button
                  type="link"
                  onClick={handleSendCode}
                  loading={sendingCode}
                  disabled={countdown > 0 || !emailVerification.canSend}
                >
                  {countdown > 0 ? `${countdown}s` : '獲取驗證碼'}
                </Button>
              }
            />
          </Form.Item>
          <Form.Item
            name="newPassword"
            rules={[
              { required: true, message: '請輸入新密碼' },
              { min: 8, message: '密碼至少8位' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="新密碼（至少8位）"
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            rules={[{ required: true, message: '請確認新密碼' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="確認新密碼"
              size="large"
            />
          </Form.Item>
          <EmailCodeVerification verification={emailVerification} />
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!emailVerification.ready}
              block
              size="large"
            >
              重置密碼
            </Button>
          </Form.Item>
        </Form>
        <div style={{ textAlign: 'center' }}>
          <Link to="/login">返回登入</Link>
        </div>
      </div>
    </div>
  )
}
